import { useEffect, useState } from "react";
import { Search, Building2, Check, X, Loader2 } from "lucide-react";
import api from "../../lib/api";

// value: { id, name, city?, state?, name_only? } | null
export function DepartmentSearch({ value, noDepartment, onSelect, onNone, onClear, testid = "dept-search", compact = false, state = "", county = "" }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setItems([]); return undefined; }
    setLoading(true);
    const t = setTimeout(() => api.get("/departments", { params: { q: s, limit: 8, state: state || undefined, county: county || undefined } }).then(({ data }) => setItems(data.items)).catch(() => setItems([])).finally(() => setLoading(false)), 300);
    return () => clearTimeout(t);
  }, [q, state, county]);
  const s = q.trim();
  const exact = items.some((d) => d.name.toLowerCase() === s.toLowerCase());
  const pick = (d) => { onSelect({ id: d.id, name: d.name, city: d.city, state: d.state }); setQ(""); setItems([]); };
  const pickName = () => { onSelect({ id: null, name: s, name_only: true }); setQ(""); setItems([]); };

  if (value?.name) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2" data-testid={`${testid}-selected`}>
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-4 h-4 text-primary shrink-0" />
          <div className="min-w-0"><p className="text-sm font-semibold text-slate-900 truncate" data-testid={`${testid}-selected-name`}>{value.name}</p>
            <p className="text-[11px] text-slate-500">{value.name_only || !value.id ? "Not in our library yet — name only for now" : [value.city, value.state].filter(Boolean).join(", ")}</p></div>
        </div>
        <button type="button" onClick={onClear} className="text-xs font-semibold text-slate-500 hover:text-primary inline-flex items-center gap-1 shrink-0" data-testid={`${testid}-change`}><X className="w-3 h-3" /> Change</button>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="relative">
        <Search className="w-4 h-4 text-primary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 27,000+ departments — e.g. Sac Metro, Folsom, LAFD" className={`w-full text-sm border-2 border-primary/35 rounded-md bg-white pl-9 pr-8 placeholder:text-slate-500 hover:border-primary/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${compact ? "py-1.5" : "py-2"}`} data-testid={`${testid}-input`} autoComplete="off" />
        {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
      </div>
      {s.length >= 2 && (
        <ul className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden" data-testid={`${testid}-results`}>
          {items.map((d) => (
            <li key={d.id}><button type="button" onClick={() => pick(d)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50" data-testid={`${testid}-result-${d.id}`}>
              <Building2 className="w-4 h-4 text-slate-400 shrink-0" /><span className="text-sm font-semibold text-slate-800">{d.name}</span><span className="text-xs text-slate-500">{[d.city, d.county ? `${d.county} Co.` : null, d.state].filter(Boolean).join(", ")}</span>{d.has_profile && <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded shrink-0">Profile</span>}</button></li>
          ))}
          {!loading && !exact && (
            <li><button type="button" onClick={pickName} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50" data-testid={`${testid}-use-name`}>
              <Check className="w-4 h-4 text-slate-400 shrink-0" /><span className="text-sm text-slate-700">Use “{s}” as my target{items.length ? "" : " — not in our library yet"}</span></button></li>
          )}
          {!loading && !items.length && <li className="px-3 py-1.5 text-[11px] text-slate-500">Not listed? Pick “Use … as my target” — you can also request it in Department Research.</li>}
        </ul>
      )}
      {onNone && (
        <button type="button" onClick={onNone} aria-pressed={!!noDepartment} data-testid={`${testid}-none`}
          className={`rounded-full border-2 px-3.5 py-1.5 text-sm font-semibold ${noDepartment ? "border-primary bg-primary/5 text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>No department in mind yet</button>
      )}
    </div>
  );
}
